import React, { FC, useEffect, useState } from 'react'
import {
  Award,
  Bell,
  Clipboard,
  ClipboardClock,
  Hourglass,
  LoaderCircle,
  MessageSquareText,
  Signature,
  Timer,
  TimerOff,
  Wallet,
} from 'lucide-react'
import moment from 'moment'
import { useKnockFeed } from '@knocklabs/react'

import { Skeleton } from '../../../../../ui/skeleton'

import { withKnockProvider } from '../../../../../hoc/withKnockProvider'
import { cn } from '../../../../../../lib/utils'

import { KNOCK_WORKFLOWS } from '../../../../../../const/knock-workflows'
import { Button } from '../../../../../ui/button'

const PAGE_SIZE = 25

type IActivityItem = { id: string; content: string; key: string; date: string }

type KnockEntry = {
  id: string
  inserted_at: string
  read_at: string | null
  source: {
    key: string
  }
  blocks: {
    name: string
    rendered: string
  }[]
  data: {}
}

const getContent = (entry: KnockEntry) => {
  return (
    (entry.blocks?.find((b) => b.name === 'body') as { rendered?: string })?.rendered ??
    entry.source?.key ??
    'Notification'
  )
}

interface ActivityIconProps {
  workflowKey: string
}

const ActivityIcon: FC<ActivityIconProps> = ({ workflowKey }) => {
  return workflowKey === KNOCK_WORKFLOWS.NEW_MESSAGE ? (
    <MessageSquareText className="w-5" />
  ) : workflowKey === KNOCK_WORKFLOWS.ORGANIZER_PUBLISHES_NEW_EVENT ? (
    <Bell className="w-5" />
  ) : workflowKey === KNOCK_WORKFLOWS.RESULTS_PUBLISHED ? (
    <Award className="w-5" />
  ) : workflowKey === KNOCK_WORKFLOWS.SEND_REQUEST_PAYMENT ? (
    <Wallet className="w-5" />
  ) : workflowKey?.includes('sign') ? (
    <Signature className="w-5" />
  ) : workflowKey?.includes('expires') ? (
    <Hourglass className="w-5" />
  ) : workflowKey?.includes('registrations-close') ? (
    <TimerOff className="w-5" />
  ) : workflowKey?.includes('registrations-open') ? (
    <Timer className="w-5" />
  ) : workflowKey?.includes('ride-time') ? (
    <ClipboardClock className="w-5" />
  ) : (
    <Clipboard className="w-5" />
  )
}

interface ActivityTabProps {
  type: 'rider' | 'registrar'
  userId: string
}

const ActivityTab: FC<ActivityTabProps> = ({ type }) => {
  const { feedClient, useFeedStore } = useKnockFeed()
  const { loading } = useFeedStore()

  const [items, setItems] = useState<IActivityItem[]>([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [afterCursor, setAfterCursor] = useState<string>('')

  const loadMore = async () => {
    setIsLoadingMore(true)

    try {
      const response = await feedClient.fetch({
        page_size: PAGE_SIZE,
        after: afterCursor,
      })

      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id))
        const uniqueNewItems = response?.data?.entries
          ?.filter((item: KnockEntry) => !existingIds.has(item.id))
          .map((entry: KnockEntry) => {
            return {
              id: entry.id,
              key: entry.source.key,
              content: getContent(entry),
              date: entry.inserted_at,
            }
          })

        return [...prev, ...uniqueNewItems]
      })
      setAfterCursor(response?.data?.page_info?.after ?? '')
    } finally {
      setIsLoadingMore(false)
    }
  }

  useEffect(() => {
    const getFeed = async () => {
      try {
        const newItems: IActivityItem[] = []

        const response = await feedClient.fetch({
          page_size: PAGE_SIZE,
        })

        response?.data.entries?.forEach((entry: KnockEntry) => {
          newItems.push({
            id: entry.id,
            key: entry.source.key,
            content: getContent(entry),
            date: entry.inserted_at,
          })
        })
        setItems(newItems)
        setAfterCursor(response?.data?.page_info?.after ?? '')
      } catch (error) {
        console.error('error: ', error)
      }
    }
    getFeed().then()
  }, [feedClient])

  if (loading && !isLoadingMore) {
    return (
      <ul className="flex flex-col gap-7 w-full">
        {Array.from({ length: 4 }).map((_, index) => (
          <li key={index} className="flex items-center gap-5">
            <Skeleton className="h-[45px] w-[45px] min-w-[45px] rounded-full" />
            <div className="flex-1 flex flex-col gap-1">
              <Skeleton className="h-5 w-1/2 md:w-1/4" />
              <Skeleton className="h-5 w-full md:w-10/12" />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return items?.[0] ? (
    <div className="flex flex-col gap-5 items-center">
      <ul className="flex flex-col gap-7 w-full">
        {items.map((item) => (
          <li className="flex items-center gap-5">
            <div
              className={cn(
                'flex items-center justify-center w-[45px] min-w-[45px] h-[45px] rounded-full',
                type === 'registrar'
                  ? 'text-PurpleThemeColor bg-PurpleThemeColor/5'
                  : 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
              )}
            >
              <ActivityIcon workflowKey={item.key} />
            </div>
            <div>
              <p className="text-base text-black/30">
                {moment(item.date).format('h:mm a, DD MMM')}
              </p>
              <div
                className="text-base text-black"
                dangerouslySetInnerHTML={{ __html: item.content }}
              />
            </div>
          </li>
        ))}
      </ul>
      {afterCursor && (
        <Button variant="red" disabled={isLoadingMore} onClick={loadMore}>
          {isLoadingMore && <LoaderCircle className="w-4 animate-spin" />}
          Show more
        </Button>
      )}
    </div>
  ) : (
    <p className="text-center text-black/50">No activity</p>
  )
}

export default withKnockProvider(ActivityTab)
